import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ClipboardList, Loader2, AlertCircle,
  CheckCircle2, MessageSquare, FileText, Eye, EyeOff, RefreshCw,
  ClipboardCheck, XCircle, Inbox, ArrowLeft,
  User, GraduationCap, Briefcase, Languages, Lightbulb,
  LayoutList, Monitor,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { formatFieldValue } from '../utils/formatFieldValue';
import { getInitials } from '../utils/getInitials';
import {
  useInstructorCVQueue,
  useInstructorCVDetail,
  useClaimCV,
  useUnclaimCV,
  useSaveCVEdits,
  useApproveCV,
  useRequestCVChanges,
} from '../hooks/cabin/useCabinCVReview';
import { CVCanvas } from '../components/cv/CVCanvasPreview';
import { seedSelectionState } from '../utils/cvSelectionState';
import { useInstructorCVPhoto } from '../hooks/useInstructorCVPhoto';

// ── Helpers ───────────────────────────────────────────────────────────────────

function interpolate(str, vars) {
  if (!str) return str;
  return Object.entries(vars).reduce(
    (s, [k, v]) => s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v ?? '')),
    str
  );
}

// ── Review ownership helpers ──────────────────────────────────────────────────
// Backend (fix/cabin-cv-inceleme-sahiplik): edits/approve/request-changes/unclaim
// yalnizca reviewer_instructor_id === caller.user_id (veya rol admin/superadmin)
// VE (edits/approve/request-changes/unclaim icin) status === 'in_review' iken
// izinli; aksi halde 403 review_forbidden / 409 invalid_state_transition doner.
// Bu iki fonksiyon UI'da AYNI kurali onceden hesaplayip butonlari pasif +
// gerekce metniyle gosterir — backend'in reddedecegi hicbir aksiyon aktif
// gorunmez.

function isCVReviewOwner(detail, user) {
  if (!detail || !user) return false;
  const role = user.role;
  if (role === 'admin' || role === 'superadmin') return true;
  const reviewerId = detail.reviewer_instructor_id;
  const currentUserId = user.user_id;
  return !!reviewerId && !!currentUserId && reviewerId === currentUserId;
}

// Aksiyonun (edits/approve/request-changes/unclaim) pasif olma gerekcesi.
// null donerse aksiyon aktif (in_review + owner).
function getCVActionLockedReasonKey(status, isOwner) {
  if (status === 'in_review') return isOwner ? null : 'other_reviewer';
  if (status === 'pending_review') return 'pending';
  if (status === 'approved' || status === 'changes_requested') return 'completed';
  return 'unavailable';
}

// Backend 403/409 hata govdesinden (detail: {error_code, message_tr, message_en})
// kullaniciya gosterilecek mesaji cikarir. Backend mesaj saglamazsa error_code'a
// gore anlamli bir fallback kullanir, o da yoksa genel hata mesaji.
function extractCVActionErrorMessage(err, t) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    if (detail.message_tr || detail.message_en) {
      return detail.message_tr || detail.message_en;
    }
    if (detail.error_code === 'review_forbidden' || detail.error_code === 'unclaim_forbidden') {
      return t('instructor.cv.toast.review_forbidden');
    }
    if (detail.error_code === 'invalid_state_transition') {
      return t('instructor.cv.toast.invalid_state');
    }
  }
  if (typeof detail === 'string' && detail) return detail;
  return t('instructor.cv.toast.error');
}

// ── Status badge config (WCAG: icon + text + color, not color alone) ─────────

const STATUS_CONFIG = {
  pending_review: {
    icon: Inbox,
    bgClass: 'bg-amber-50',
    borderClass: 'border-amber-300',
    textClass: 'text-amber-700',
    dotClass: 'bg-amber-400',
    i18nKey: 'instructor.cv.status.pending_review',
  },
  in_review: {
    icon: ClipboardList,
    bgClass: 'bg-blue-50',
    borderClass: 'border-blue-200',
    textClass: 'text-[var(--brand-primary)]',
    dotClass: 'bg-[var(--brand-primary)]',
    i18nKey: 'instructor.cv.status.in_review',
  },
  approved: {
    icon: CheckCircle2,
    bgClass: 'bg-emerald-50',
    borderClass: 'border-emerald-300',
    textClass: 'text-emerald-700',
    dotClass: 'bg-emerald-400',
    i18nKey: 'instructor.cv.status.approved',
  },
  changes_requested: {
    icon: MessageSquare,
    bgClass: 'bg-rose-50',
    borderClass: 'border-rose-300',
    textClass: 'text-rose-700',
    dotClass: 'bg-rose-400',
    i18nKey: 'instructor.cv.status.changes_requested',
  },
};

function StatusBadge({ status, t }) {
  const cfg = STATUS_CONFIG[status];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span
      role="status"
      aria-label={t(cfg.i18nKey)}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-bold ${cfg.bgClass} ${cfg.borderClass} ${cfg.textClass}`}
    >
      <Icon size={12} aria-hidden="true" />
      {t(cfg.i18nKey)}
    </span>
  );
}

// ── Section editable block ────────────────────────────────────────────────────

const SECTION_KEYS = ['kisisel', 'egitim', 'is_deneyimi', 'dil', 'motivasyon'];

function SectionEditor({ sectionKey, original, edited, onChange, showMerged, t, disabled = false }) {
  const source = showMerged ? { ...(original || {}), ...(edited || {}) } : (original || {});
  const editedSection = edited || {};

  const allFieldKeys = Array.from(new Set([
    ...Object.keys(source),
    ...Object.keys(editedSection),
  ])).filter(k => typeof source[k] !== 'object' || source[k] === null);

  if (allFieldKeys.length === 0) {
    return (
      <p className="text-xs text-gray-400 italic px-1">{t('instructor.cv.queue.empty_sub')}</p>
    );
  }

  return (
    <div className="space-y-2">
      {allFieldKeys.map((field) => {
        const origVal = original?.[field];
        const editVal = editedSection[field];
        const displayVal = showMerged ? (editVal ?? origVal) : origVal;
        const rawVal = editVal !== undefined ? editVal : displayVal;
        const isBoolean = typeof rawVal === 'boolean' || typeof origVal === 'boolean';

        return (
          <div key={field} className="flex flex-col gap-0.5">
            <label
              htmlFor={isBoolean ? undefined : `edit-${sectionKey}-${field}`}
              className="text-xs font-bold text-gray-500 tracking-wide"
            >
              {/* Use toLocaleUpperCase('tr-TR') to avoid CSS text-transform İ/I bug */}
              {field.replace(/_/g, ' ').toLocaleUpperCase('tr-TR')}
            </label>
            {isBoolean ? (
              // Boolean fields are read-only — editing would send string "Evet" to backend
              <span
                aria-readonly="true"
                className="inline-flex items-center px-3 py-2 rounded-lg border-2 border-gray-100 bg-gray-50 text-sm font-medium text-gray-500 min-h-[40px]"
              >
                {formatFieldValue(rawVal, t)}
              </span>
            ) : (
              <input
                id={`edit-${sectionKey}-${field}`}
                type="text"
                value={formatFieldValue(rawVal, t)}
                onChange={(e) => onChange(sectionKey, field, e.target.value)}
                disabled={disabled}
                className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 text-sm font-medium bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)] focus:border-[var(--brand-primary)] hover:border-gray-300 transition-colors min-h-[40px] disabled:bg-gray-50 disabled:text-gray-400"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Panel tab definitions — defined at module level (stable, no runtime deps) ──

const PANEL_TABS = [
  { key: 'kisisel',      icon: User,            labelKey: 'instructor.cv.panel.section.kisisel' },
  { key: 'egitim',       icon: GraduationCap,   labelKey: 'instructor.cv.panel.section.egitim' },
  { key: 'is_deneyimi',  icon: Briefcase,       labelKey: 'instructor.cv.panel.section.is_deneyimi' },
  { key: 'dil',          icon: Languages,       labelKey: 'instructor.cv.panel.section.dil' },
  { key: 'motivasyon',   icon: Lightbulb,       labelKey: 'instructor.cv.panel.section.motivasyon' },
  { key: 'assessment',   icon: ClipboardCheck,  labelKey: 'instructor.cv.panel.section.assessment' },
];

// ── Review panel (right side) ─────────────────────────────────────────────────

function ReviewPanel({ cvId, t, lang: _lang, toast }) {
  const { user } = useAuth();
  const { data: detail, isLoading, isError, refetch } = useInstructorCVDetail(cvId);
  const claimMutation = useClaimCV();
  const unclaimMutation = useUnclaimCV();
  const saveEditsMutation = useSaveCVEdits();
  const approveMutation = useApproveCV();
  const requestChangesMutation = useRequestCVChanges();

  const [showMerged, setShowMerged] = useState(true);
  const [editedSections, setEditedSections] = useState({});
  const [instructorNote, setInstructorNote] = useState('');
  const [studentMessage, setStudentMessage] = useState('');
  const [messageError, setMessageError] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved
  const [activeTab, setActiveTab] = useState('kisisel');
  // 'form' | 'preview'
  const [viewMode, setViewMode] = useState('form');

  // PANEL_TABS is defined at module level above ReviewPanel

  // Seed state when detail loads
  const prevCvId = React.useRef(null);
  React.useEffect(() => {
    if (detail && cvId !== prevCvId.current) {
      prevCvId.current = cvId;
      setEditedSections(detail.instructor_edits || {});
      setInstructorNote(detail.instructor_note || '');
      setStudentMessage('');
      setMessageError(false);
      setSaveStatus('idle');
      setViewMode('form');
    }
  }, [detail, cvId]);

  // Photo blob for CV preview (instructor endpoint)
  const previewFotoUrl = detail?.merged?.foto_url || detail?.original?.foto_url || null;
  const fotoBlobUrl = useInstructorCVPhoto(cvId, previewFotoUrl);

  // Memoize selectionState — avoids creating a new object on every render
  const selectionState = useMemo(
    () => seedSelectionState(detail?.merged || detail?.original || {}),
    [detail]
  );

  const handleSectionChange = useCallback((section, field, value) => {
    setEditedSections((prev) => ({
      ...prev,
      [section]: { ...(prev[section] || {}), [field]: value },
    }));
  }, []);

  // Ownership/durum uyusmazligi (403 review_forbidden / unclaim_forbidden,
  // 409 invalid_state_transition) baska bir eğitmenin CV'yi araya girip claim
  // etmis/onaylamis olabilecegi bir yaris durumunu isaret eder — anlamli
  // mesaj gosterdikten sonra detay yeniden cekilir ki ekran (butonlarin
  // aktif/pasif durumu dahil) guncel gercek duruma donsun.
  const handleMutationError = (err) => {
    toast.error(extractCVActionErrorMessage(err, t));
    const httpStatus = err?.response?.status;
    if (httpStatus === 403 || httpStatus === 409) {
      refetch();
    }
  };

  const handleSaveEdits = async () => {
    setSaveStatus('saving');
    try {
      await saveEditsMutation.mutateAsync({
        id: cvId,
        instructor_edits: editedSections,
        instructor_note: instructorNote,
      });
      setSaveStatus('saved');
      toast.success(t('instructor.cv.toast.edits_saved'));
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch (err) {
      setSaveStatus('idle');
      handleMutationError(err);
    }
  };

  const handleClaim = async () => {
    try {
      await claimMutation.mutateAsync(cvId);
      toast.success(t('instructor.cv.toast.claimed'));
    } catch (err) {
      handleMutationError(err);
    }
  };

  const handleUnclaim = async () => {
    try {
      await unclaimMutation.mutateAsync(cvId);
      toast.success(t('instructor.cv.toast.unclaimed'));
    } catch (err) {
      handleMutationError(err);
    }
  };

  const handleApprove = async () => {
    try {
      await approveMutation.mutateAsync(cvId);
      toast.success(t('instructor.cv.toast.approved'));
    } catch (err) {
      handleMutationError(err);
    }
  };

  const handleRequestChanges = async () => {
    if (!studentMessage.trim()) {
      setMessageError(true);
      return;
    }
    setMessageError(false);
    try {
      await requestChangesMutation.mutateAsync({ id: cvId, student_message: studentMessage });
      toast.success(t('instructor.cv.toast.changes_sent'));
      setStudentMessage('');
    } catch (err) {
      handleMutationError(err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <Loader2 size={28} className="animate-spin text-[var(--brand-primary)]" aria-hidden="true" />
        <p className="text-sm text-gray-500 font-medium">{t('instructor.cv.queue.loading')}</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center px-4">
        <AlertCircle size={28} className="text-rose-400" aria-hidden="true" />
        <p className="text-sm text-gray-600 font-medium">{t('instructor.cv.queue.error')}</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 rounded-xl border-2 border-gray-200 text-sm font-bold text-gray-600 hover:border-gray-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400 min-h-[40px]"
        >
          <RefreshCw size={14} className="inline mr-1.5" aria-hidden="true" />
          {t('cv.status.error_retry')}
        </button>
      </div>
    );
  }

  if (!detail) return null;

  const status = detail.status;
  const canClaim = status === 'pending_review';
  const isLocked = status === 'approved' || status === 'changes_requested';

  // Sahiplik: reviewer_instructor_id === giris yapan egitmenin user_id'si,
  // ya da rol admin/superadmin (bkz. backend _require_cv_review_ownership).
  const isReviewOwner = isCVReviewOwner(detail, user);
  // edits/approve/request-changes/unclaim backend'de YALNIZCA status==='in_review'
  // VE sahiplik saglaniyorsa izinli — bu iki kosuldan biri eksikse buton pasif
  // olur ve nedeni yaninda gosterilir (bkz. lockedReasonKey/lockedReasonText).
  const isReviewActionAllowed = status === 'in_review' && isReviewOwner;
  const lockedReasonKey = isReviewActionAllowed ? null : getCVActionLockedReasonKey(status, isReviewOwner);
  const lockedReasonText = lockedReasonKey ? t(`instructor.cv.action.locked_reason.${lockedReasonKey}`) : null;
  const lockedReasonId = 'cv-action-locked-reason';

  // Öğrenciye mesaj alanı: in_review sırasında (yazılabilir) veya
  // changes_requested sonrasında (salt-okunur geçmiş) görünür; gönderim izni
  // ise (ownership + in_review) isReviewActionAllowed'a bağlı.
  const isMessageVisible = status === 'in_review' || status === 'changes_requested';
  const isMessageDisabled = !isReviewActionAllowed;

  const originalCV = detail.original || {};
  const mergedCV = detail.merged || detail.original || {};

  return (
    <div
      style={{ height: 'calc(100vh - 220px)', display: 'flex', flexDirection: 'column' }}
    >
      {/* KATMAN 1 — Sabit başlık şeridi */}
      <div style={{ padding: '14px 20px 0', flexShrink: 0 }}>
        <div className="flex items-center justify-between flex-wrap gap-3" style={{ marginBottom: 10 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 900, color: '#13233F', margin: 0, lineHeight: 1.2 }}>
              {detail.student_display_name || detail.student_email}
            </h2>
            <p className="text-xs text-gray-500" style={{ margin: '2px 0 0' }}>{detail.student_email}</p>
          </div>
          <StatusBadge status={status} t={t} />
        </div>

        {/* Satır 2: Original/Merged toggle (sadece form görünümünde) + Form/Önizleme toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          {/* Original / Merged toggle — sadece form görünümünde göster */}
          {viewMode === 'form' && (
            <div className="flex items-center gap-2 bg-gray-100 rounded-xl p-1 w-fit">
              <button
                type="button"
                onClick={() => setShowMerged(false)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all min-h-[36px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]
                  ${!showMerged ? 'bg-white border-2 border-gray-200 text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                aria-pressed={!showMerged}
              >
                <Eye size={13} aria-hidden="true" />
                {t('instructor.cv.panel.original_toggle')}
              </button>
              <button
                type="button"
                onClick={() => setShowMerged(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all min-h-[36px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]
                  ${showMerged ? 'bg-white border-2 border-gray-200 text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                aria-pressed={showMerged}
              >
                <EyeOff size={13} aria-hidden="true" />
                {t('instructor.cv.panel.merged_toggle')}
              </button>
            </div>
          )}

          {/* Form Görünümü / CV Önizleme toggle */}
          <div
            role="group"
            aria-label={t('instructor.cv.panel.view_toggle_label')}
            className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 ml-auto"
          >
            <button
              type="button"
              onClick={() => setViewMode('form')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all min-h-[36px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]
                ${viewMode === 'form' ? 'bg-white border-2 border-gray-200 text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              aria-pressed={viewMode === 'form'}
            >
              <LayoutList size={13} aria-hidden="true" />
              {t('instructor.cv.panel.view_form')}
            </button>
            <button
              type="button"
              onClick={() => setViewMode('preview')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all min-h-[36px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]
                ${viewMode === 'preview' ? 'bg-white border-2 border-gray-200 text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              aria-pressed={viewMode === 'preview'}
            >
              <Monitor size={13} aria-hidden="true" />
              {t('instructor.cv.panel.view_preview')}
            </button>
          </div>
        </div>
      </div>

      {/* KATMAN 2 — CV Önizleme veya Yatay 6 sekme + sekme içeriği */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>

        {/* CV Önizleme modu: salt-görüntü canvas */}
        {viewMode === 'preview' && (
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              minHeight: 0,
              padding: '12px 16px 8px',
            }}
            aria-label={t('instructor.cv.panel.view_preview')}
          >
            <CVCanvas
              cvData={mergedCV}
              selectionState={selectionState}
              isDeneyimleri={mergedCV.is_deneyimleri || []}
              fotoBlobUrl={fotoBlobUrl}
              templateId={mergedCV.template_id || 'classic'}
              t={t}
              canvasEdit={undefined}
            />
          </div>
        )}

        {/* Form görünümü: sekmeler + bölüm editörleri */}
        {viewMode === 'form' && (
          <>
        {/* Sekme satırı */}
        <div
          role="tablist"
          aria-label={t('instructor.cv.panel.tabs_label')}
          style={{ display: 'flex', gap: 4, padding: '0 16px', flexShrink: 0, overflowX: 'auto' }}
        >
          {PANEL_TABS.map(({ key, icon: TabIcon, labelKey }) => {
            const isActive = activeTab === key;
            return (
              <button
                key={key}
                id={`panel-tab-${key}`}
                role="tab"
                aria-selected={isActive}
                aria-controls={`panel-tabpanel-${key}`}
                type="button"
                onClick={() => setActiveTab(key)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '7px 12px', borderRadius: '10px 10px 0 0',
                  fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700,
                  cursor: 'pointer', whiteSpace: 'nowrap',
                  border: isActive ? '2px solid #E5E7EB' : '2px solid transparent',
                  borderBottom: isActive ? '2px solid #fff' : '2px solid transparent',
                  background: isActive ? '#fff' : '#F3F4F6',
                  color: isActive ? '#13233F' : '#6B7280',
                  boxShadow: isActive ? '0 -2px 6px rgba(0,0,0,0.04)' : 'none',
                  transition: 'all 0.15s',
                  outline: 'none',
                  marginBottom: isActive ? -2 : 0,
                  position: 'relative',
                  zIndex: isActive ? 2 : 1,
                }}
              >
                <TabIcon size={13} strokeWidth={2} aria-hidden="true" />
                {t(labelKey)}
              </button>
            );
          })}
        </div>

        {/* Sekme içerik alanı — yalnızca bu scroll eder */}
        <div
          role="tabpanel"
          id={`panel-tabpanel-${activeTab}`}
          aria-labelledby={`panel-tab-${activeTab}`}
          style={{
            flex: 1,
            overflowY: 'auto',
            minHeight: 0,
            padding: '16px 16px 8px',
            border: '2px solid #E5E7EB',
            borderRadius: '0 10px 10px 10px',
            margin: '0 16px',
            background: '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}
        >
          {/* Section sekmeleri: kisisel, egitim, is_deneyimi, dil, motivasyon */}
          {SECTION_KEYS.includes(activeTab) && (
            <div>
              <h3 className="text-xs font-black text-gray-600 tracking-wide" style={{ marginBottom: 8 }}>
                {String(t(`instructor.cv.panel.section.${activeTab}`)).toLocaleUpperCase('tr-TR')}
              </h3>
              <SectionEditor
                sectionKey={activeTab}
                original={(showMerged ? mergedCV : originalCV)[activeTab] || {}}
                edited={editedSections[activeTab] || {}}
                onChange={handleSectionChange}
                showMerged={showMerged}
                t={t}
                disabled={!isReviewActionAllowed}
              />
            </div>
          )}

          {/* Değerlendirme sekmesi: dahili not + öğrenci mesajı */}
          {activeTab === 'assessment' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Dahili not */}
              <div>
                <label
                  htmlFor="instructor-note"
                  className="block text-xs font-bold text-gray-600 tracking-wide"
                  style={{ marginBottom: 6 }}
                >
                  {String(t('instructor.cv.panel.note_label')).toLocaleUpperCase('tr-TR')}
                </label>
                <textarea
                  id="instructor-note"
                  value={instructorNote}
                  onChange={(e) => setInstructorNote(e.target.value)}
                  placeholder={t('instructor.cv.panel.note_placeholder')}
                  rows={4}
                  disabled={!isReviewActionAllowed}
                  className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 text-sm font-medium bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)] focus:border-[var(--brand-primary)] resize-none transition-colors disabled:bg-gray-50 disabled:text-gray-400"
                />
              </div>

              {/* Öğrenciye mesaj — in_review sırasında yazılabilir, changes_requested'ta salt-okunur geçmiş */}
              {isMessageVisible && (
                <div>
                  <label
                    htmlFor="student-message"
                    className="block text-xs font-bold text-rose-600 tracking-wide"
                    style={{ marginBottom: 6 }}
                  >
                    {String(t('instructor.cv.panel.message_label')).toLocaleUpperCase('tr-TR')}
                  </label>
                  <textarea
                    id="student-message"
                    value={studentMessage}
                    onChange={(e) => {
                      setStudentMessage(e.target.value);
                      if (e.target.value.trim()) setMessageError(false);
                    }}
                    placeholder={t('instructor.cv.panel.message_placeholder')}
                    rows={4}
                    disabled={isMessageDisabled}
                    aria-invalid={messageError}
                    aria-describedby={messageError ? 'student-message-error' : undefined}
                    className={`w-full px-3 py-2 rounded-lg border-2 text-sm font-medium bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 resize-none transition-colors disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed
                      ${messageError ? 'border-rose-400' : 'border-rose-200 focus:border-rose-400'}`}
                  />
                  {messageError && (
                    <p
                      id="student-message-error"
                      role="alert"
                      className="flex items-center gap-1 text-xs text-rose-600 font-medium mt-1"
                    >
                      <AlertCircle size={12} aria-hidden="true" />
                      {t('instructor.cv.panel.message_required')}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
          </>
        )}
      </div>

      {/* KATMAN 3 — Sticky aksiyon barı */}
      <div
        style={{
          flexShrink: 0,
          background: '#fff',
          borderTop: '2px solid #E6ECF3',
          padding: '12px 20px',
          borderRadius: '0 0 20px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        {/* Sol: Düzenlemeleri Kaydet — her zaman gösterilir; sahiplik/durum
            kuralına (isReviewActionAllowed) uymuyorsa pasif olur ve nedeni
            aria-describedby ile sağdaki ortak gerekçe metnine bağlanır. */}
        <div style={{ flexShrink: 0 }}>
          <motion.button
            type="button"
            onClick={handleSaveEdits}
            disabled={!isReviewActionAllowed || saveEditsMutation.isPending || saveStatus === 'saved'}
            aria-describedby={lockedReasonKey ? lockedReasonId : undefined}
            whileHover={{ scale: (!isReviewActionAllowed || saveEditsMutation.isPending) ? 1 : 1.02 }}
            whileTap={{ scale: (!isReviewActionAllowed || saveEditsMutation.isPending) ? 1 : 0.97 }}
            className="flex items-center justify-center gap-2 py-2 px-4 rounded-xl border-2 border-gray-200 bg-white text-gray-700 font-bold text-sm hover:border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400 min-h-[44px]"
          >
            {saveEditsMutation.isPending ? (
              <>
                <Loader2 size={15} className="animate-spin" aria-hidden="true" />
                {t('instructor.cv.panel.saving')}
              </>
            ) : saveStatus === 'saved' ? (
              <>
                <CheckCircle2 size={15} className="text-emerald-500" aria-hidden="true" />
                {t('instructor.cv.panel.saved')}
              </>
            ) : (
              <>
                <FileText size={15} aria-hidden="true" />
                {t('instructor.cv.panel.save_edits')}
              </>
            )}
          </motion.button>
        </div>

        {/* Sag: durum bazlı birincil aksiyonlar + (varsa) ortak pasif gerekçesi */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          {/* Neden metni: render koşulundan türetilir (state'e/tıklamaya bağlı
              değil) — pasif butonlar disabled iken onClick hiç çalışmadığından
              nedeni state güncellemesiyle göstermek güvenilmezdi (önceki tur
              regresyonu). Butonlar aria-describedby ile buna bağlanır. */}
          {lockedReasonText && (
            <p
              id={lockedReasonId}
              className="text-xs text-gray-500 font-medium"
              style={{ margin: 0, textAlign: 'right' }}
            >
              {lockedReasonText}
            </p>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {/* isLocked: read-only badge */}
            {isLocked && (
              <StatusBadge status={status} t={t} />
            )}

            {/* pending_review → Sahiplen */}
            {canClaim && (
              <motion.button
                type="button"
                onClick={handleClaim}
                disabled={claimMutation.isPending}
                whileHover={{ scale: claimMutation.isPending ? 1 : 1.02 }}
                whileTap={{ scale: 0.97 }}
                className="flex items-center justify-center gap-2 py-2 px-4 rounded-xl font-bold text-sm border-2 border-[var(--brand-primary-shadow)] shadow-[2px_2px_0px_0px_rgba(0,0,0,0.15)] hover:bg-[var(--brand-primary-hover)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-h-[44px]"
                style={{ background: '#1D6FA4', color: '#fff' }}
              >
                {claimMutation.isPending ? (
                  <>
                    <Loader2 size={15} className="animate-spin" aria-hidden="true" />
                    {t('instructor.cv.action.claiming')}
                  </>
                ) : (
                  <>
                    <ClipboardCheck size={15} aria-hidden="true" />
                    {t('instructor.cv.action.claim')}
                  </>
                )}
              </motion.button>
            )}

            {/* Unclaim/Onayla/Değişiklik İste — her zaman gösterilir; yalnızca
                status==='in_review' VE sahiplik varken (isReviewActionAllowed)
                aktif, aksi halde pasif + ortak gerekçe (yukarıda). */}
            <motion.button
              type="button"
              onClick={handleUnclaim}
              disabled={!isReviewActionAllowed || unclaimMutation.isPending}
              aria-describedby={lockedReasonKey ? lockedReasonId : undefined}
              whileHover={{ scale: (!isReviewActionAllowed || unclaimMutation.isPending) ? 1 : 1.02 }}
              whileTap={{ scale: (!isReviewActionAllowed || unclaimMutation.isPending) ? 1 : 0.97 }}
              className="flex items-center justify-center gap-2 py-2 px-3 rounded-xl bg-white text-gray-700 font-bold text-sm border-2 border-gray-200 hover:border-gray-300 transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400 min-h-[44px]"
            >
              {unclaimMutation.isPending ? (
                <Loader2 size={15} className="animate-spin" aria-hidden="true" />
              ) : (
                <XCircle size={15} aria-hidden="true" />
              )}
              {t('instructor.cv.action.unclaim')}
            </motion.button>

            <motion.button
              type="button"
              onClick={handleApprove}
              disabled={!isReviewActionAllowed || approveMutation.isPending}
              aria-describedby={lockedReasonKey ? lockedReasonId : undefined}
              whileHover={{ scale: (!isReviewActionAllowed || approveMutation.isPending) ? 1 : 1.02 }}
              whileTap={{ scale: (!isReviewActionAllowed || approveMutation.isPending) ? 1 : 0.97 }}
              className="flex items-center justify-center gap-2 py-2 px-4 rounded-xl bg-emerald-600 text-white font-bold text-sm border-2 border-emerald-700 shadow-[2px_2px_0_#047857] hover:bg-emerald-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 min-h-[44px]"
            >
              {approveMutation.isPending ? (
                <>
                  <Loader2 size={15} className="animate-spin" aria-hidden="true" />
                  {t('instructor.cv.action.approving')}
                </>
              ) : (
                <>
                  <CheckCircle2 size={15} aria-hidden="true" />
                  {t('instructor.cv.action.approve')}
                </>
              )}
            </motion.button>

            <motion.button
              type="button"
              onClick={handleRequestChanges}
              disabled={!isReviewActionAllowed || requestChangesMutation.isPending}
              aria-describedby={lockedReasonKey ? lockedReasonId : undefined}
              whileHover={{ scale: (!isReviewActionAllowed || requestChangesMutation.isPending) ? 1 : 1.02 }}
              whileTap={{ scale: (!isReviewActionAllowed || requestChangesMutation.isPending) ? 1 : 0.97 }}
              className="flex items-center justify-center gap-2 py-2 px-4 rounded-xl bg-amber-500 text-white font-bold text-sm border-2 border-amber-600 shadow-[2px_2px_0_#d97706] hover:bg-amber-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500 min-h-[44px]"
            >
              {requestChangesMutation.isPending ? (
                <>
                  <Loader2 size={15} className="animate-spin" aria-hidden="true" />
                  {t('instructor.cv.action.requesting')}
                </>
              ) : (
                <>
                  <MessageSquare size={15} aria-hidden="true" />
                  {t('instructor.cv.action.request_changes')}
                </>
              )}
            </motion.button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Filter tabs ───────────────────────────────────────────────────────────────

const FILTER_TABS = [
  { value: '', labelKey: 'instructor.cv.filter.all', icon: ClipboardList },
  { value: 'pending_review', labelKey: 'instructor.cv.filter.pending_review', icon: Inbox },
  { value: 'in_review', labelKey: 'instructor.cv.filter.in_review', icon: ClipboardList },
  { value: 'approved', labelKey: 'instructor.cv.filter.approved', icon: CheckCircle2 },
  { value: 'changes_requested', labelKey: 'instructor.cv.filter.changes_requested', icon: MessageSquare },
];

// ── Main page ─────────────────────────────────────────────────────────────────

export default function InstructorCVReviews() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const toast = useToast();

  const [activeFilter, setActiveFilter] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const { data: queue, isLoading: queueLoading, isError: queueError, refetch: refetchQueue } = useInstructorCVQueue({
    status: activeFilter,
  });

  const handleFilterChange = (value) => {
    setActiveFilter(value);
    setSelectedId(null);
  };

  const handleSelectCV = (id) => {
    setSelectedId(id);
  };

  return (
    <div
      data-testid="instructor-cv-reviews-page"
      style={{ padding: '34px 40px 56px', maxWidth: 'var(--interview-content-max-w)', width: '100%' }}
    >
      {/* Proto header */}
      <div style={{ marginBottom: 26 }}>
        {/* Geri butonu */}
        <button
          type="button"
          onClick={() => navigate('/instructor')}
          className="inline-flex items-center gap-1.5 mb-4 px-3 py-1.5 rounded-xl border-2 border-gray-200 bg-white text-gray-600 font-semibold text-sm hover:bg-gray-50 hover:border-gray-300 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] shadow-[1px_1px_0px_0px_rgba(0,0,0,0.06)]"
          aria-label={t('common.back')}
        >
          <ArrowLeft size={16} aria-hidden="true" />
          {t('common.back')}
        </button>
        <div style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: 1, color: '#5848C2', marginBottom: 7 }}>
          {lang === 'tr' ? 'EĞİTMEN PANELİ' : 'INSTRUCTOR PANEL'}
        </div>
        <h1 style={{ fontFamily: "'Fredoka','Nunito',sans-serif", fontWeight: 600, fontSize: 33, margin: 0, color: '#13233F' }}>
          {t('instructor.cv.page_title')}
        </h1>
        <p style={{ margin: '7px 0 0', color: '#6B7B92', fontSize: 15 }}>
          {t('instructor.cv.page_sub')}
        </p>
      </div>

      {/* Filter tabs */}
      <div
        role="tablist"
        aria-label={t('instructor.cv.page_title')}
        style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}
      >
        {FILTER_TABS.map(({ value, labelKey, icon: Icon }) => {
          const isActive = activeFilter === value;
          return (
            <button
              key={value}
              role="tab"
              aria-selected={isActive}
              type="button"
              onClick={() => handleFilterChange(value)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '7px 14px', borderRadius: 8, border: 'none',
                fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
                background: isActive ? '#14B4CC' : '#fff',
                color: isActive ? '#fff' : '#6B7B92',
                outline: 'none', minHeight: 36,
                boxShadow: isActive ? '0 4px 12px -4px rgba(20,180,204,.5)' : 'none',
                transition: 'all 0.15s',
              }}
            >
              <Icon size={13} aria-hidden="true" />
              {t(labelKey)}
            </button>
          );
        })}
      </div>

      {/* Two-column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.7fr', gap: 16 }}>
        {/* Left: CV card grid — proto 2-column */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(1,1fr)', gap: 16 }}>
          {queueLoading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 160, gap: 8 }}>
              <Loader2 size={22} className="animate-spin" style={{ color: '#14B4CC' }} aria-hidden="true" />
              <p style={{ fontSize: 14, color: '#8593A6', fontWeight: 600 }}>{t('instructor.cv.queue.loading')}</p>
            </div>
          )}

          {queueError && !queueLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 160, gap: 12, textAlign: 'center' }}>
              <AlertCircle size={24} style={{ color: '#F47272' }} aria-hidden="true" />
              <p style={{ fontSize: 14, color: '#6B7B92', fontWeight: 600 }}>{t('instructor.cv.queue.error')}</p>
              <button
                type="button"
                onClick={() => refetchQueue()}
                style={{ padding: '8px 16px', borderRadius: 10, border: '1.5px solid #DCE4ED', background: '#fff', color: '#13233F', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', minHeight: 36 }}
              >
                <RefreshCw size={13} className="inline mr-1.5" aria-hidden="true" />
                {t('cv.status.error_retry')}
              </button>
            </div>
          )}

          {!queueLoading && !queueError && (!queue || queue.length === 0) && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 160, gap: 8, textAlign: 'center', background: '#fff', border: '1px solid #E6ECF3', borderRadius: 18, padding: 24 }}>
              <Inbox size={28} style={{ color: '#CBD5E1' }} aria-hidden="true" />
              <p style={{ fontSize: 14, fontWeight: 700, color: '#8593A6', margin: 0 }}>{t('instructor.cv.queue.empty')}</p>
              <p style={{ fontSize: 12.5, color: '#9AA8BC', margin: 0 }}>{t('instructor.cv.queue.empty_sub')}</p>
            </div>
          )}

          {!queueLoading && !queueError && queue && queue.length > 0 && (
            <AnimatePresence initial={false}>
              {queue.map((item) => {
                const isSelected = selectedId === item.cv_profile_id;
                const initials = getInitials(item.student_display_name || item.student_email);
                const fmtd = item.submitted_at ? (() => {
                  try {
                    return new Intl.DateTimeFormat(lang === 'tr' ? 'tr-TR' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(item.submitted_at));
                  } catch { return item.submitted_at; }
                })() : '';
                const roundStr = item.review_round > 1
                  ? interpolate(t('instructor.cv.list.round'), { round: item.review_round })
                  : null;
                return (
                  <motion.div
                    key={item.cv_profile_id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.15 }}
                    style={{
                      background: '#fff',
                      border: isSelected ? '2px solid #5848C2' : '1px solid #E6ECF3',
                      borderRadius: 18,
                      padding: 20,
                      boxShadow: isSelected ? '0 8px 22px rgba(88,72,194,.12)' : '0 8px 22px rgba(16,40,73,.04)',
                      display: 'flex', flexDirection: 'column', gap: 16,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                    onClick={() => handleSelectCV(item.cv_profile_id)}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isSelected}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSelectCV(item.cv_profile_id); }}
                  >
                    {/* Avatar + name row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <div style={{ width: 46, height: 46, flexShrink: 0, borderRadius: 13, background: 'linear-gradient(135deg,#6B5BD6,#473AA8)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15 }} aria-hidden="true">
                        {initials}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: 15.5, color: '#13233F', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.student_display_name || item.student_email}
                        </div>
                        <div style={{ fontSize: 13, color: '#8593A6', marginTop: 2 }}>
                          {roundStr || lang === 'tr' ? (roundStr || t('instructor.cv.list.round1')) : (roundStr || 'Round 1')}
                          {fmtd ? ` · ${fmtd}` : ''}
                        </div>
                      </div>
                      <StatusBadge status={item.status} t={t} />
                    </div>
                    {/* CTA button */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleSelectCV(item.cv_profile_id); }}
                      style={{ fontFamily: 'inherit', fontSize: 14, fontWeight: 700, padding: 11, borderRadius: 11, border: 'none', background: '#13233F', color: '#fff', cursor: 'pointer', width: '100%' }}
                    >
                      {lang === 'tr' ? "CV'yi Değerlendir" : 'Review CV'}
                    </button>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </div>

        {/* Right: review panel */}
        <div>
          <div style={{ background: '#fff', border: '1px solid #E6ECF3', borderRadius: 20, padding: 0, boxShadow: '0 8px 26px rgba(16,40,73,.05)', overflow: 'hidden' }}>
            {!selectedId ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 260, gap: 12, textAlign: 'center', padding: 20 }}>
                <ClipboardList size={32} style={{ color: '#CBD5E1' }} aria-hidden="true" />
                <p style={{ fontSize: 14, color: '#9AA8BC', fontWeight: 500 }}>
                  {t('instructor.cv.select_prompt')}
                </p>
              </div>
            ) : (
              <ReviewPanel
                key={selectedId}
                cvId={selectedId}
                t={t}
                lang={lang}
                toast={toast}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
